import { closestCenter, pointerWithin, rectIntersection } from "@dnd-kit/core";

// Innermost-wins ranking for the "drop into a container" branch of the custom
// collision detection: a Hero beats the Component it lives in, which beats the
// Page. Higher number = deeper nesting = preferred drop target.
export const PARENT_SCOPE_RANK = { hero: 3, component: 2, page: 1 };

/**
 * Custom dnd-kit collision detection so nested containers (especially Heroes,
 * which live *inside* a component) become reliable drop targets.
 *
 * Plain ``closestCenter`` compares the dragged item to the center of every
 * droppable at once. Because a Hero's bounding box is nested inside its owning
 * component, the component's center is often as close (or closer) than the
 * Hero's — so dropping an outside element (e.g. a Title or Image) "into the
 * Hero" frequently resolved to the component instead, or to nothing when the
 * Hero was empty (it exposed no child sortable to land on). That is the
 * "I can't move a title/image into the hero" pain.
 *
 * Strategy:
 *   1. Find the droppables the pointer is actually inside (``pointerWithin``);
 *      fall back to rect intersection, then closestCenter, when the pointer
 *      isn't over anything (e.g. dragging in empty gutter space).
 *   2. Exclude any *container's own* sortable item node from the reorder
 *      candidates. A Hero (and a Component) renders BOTH as a sortable item
 *      (``hero:<id>`` / ``component:<id>``) inside its parent AND as a drop
 *      container (``parent:hero:<id>`` / ``parent:component:<id>``). When the
 *      pointer is inside such a container, its own item node sits under the
 *      pointer too — but treating it as a reorder anchor would drop the element
 *      BESIDE the container instead of INSIDE it. Empty-Hero / empty-Component
 *      drops would then "reorder next to the Hero", which is exactly the
 *      "I can't move a Title/Image into the Hero" pain.
 *   3. If, after that exclusion, real child sortables are under the pointer,
 *      resolve among them with closestCenter — this preserves precise
 *      reordering between siblings.
 *   4. Otherwise the pointer is over empty container space: pick the
 *      *innermost* parent container (Hero beats Component beats Page) so the
 *      drop lands where the cursor visually is, nesting the element inside it.
 *
 * @param {import("@dnd-kit/core").CollisionDetection} args dnd-kit collision args
 * @returns {Array} the resolved collision(s), highest priority first
 */
export function collisionDetectionStrategy(args) {
  const pointerHits = pointerWithin(args);
  const hits = pointerHits.length > 0 ? pointerHits : rectIntersection(args);
  if (!hits || hits.length === 0) {
    return closestCenter(args);
  }

  // The sortable item id of every container the pointer is currently inside
  // (derived from its ``parent:<scope>:<id>`` droppable). These must not act as
  // reorder anchors — see step 2 above.
  const containerItemIds = new Set(
    hits
      .filter((c) => String(c.id).startsWith("parent:"))
      .map((c) => {
        const [, scope, rawId] = String(c.id).split(":");
        return `${scope}:${rawId}`;
      }),
  );

  // Child sortables under the pointer (parent droppables and the containers'
  // own item nodes removed) are the precise reorder anchors.
  const itemHits = hits.filter((c) => {
    const sid = String(c.id);
    if (sid.startsWith("parent:")) return false;
    if (containerItemIds.has(sid)) return false;
    return true;
  });
  if (itemHits.length > 0) {
    const itemIds = new Set(itemHits.map((c) => c.id));
    const closest = closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((d) => itemIds.has(d.id)),
    });
    if (closest.length > 0) return closest;
    return [itemHits[0]];
  }

  // Only container droppables under the pointer: choose the deepest nesting so
  // the element nests inside the container the cursor is visually within.
  const parentHits = hits.filter((c) => String(c.id).startsWith("parent:"));
  if (parentHits.length > 0) {
    const ranked = parentHits
      .map((c) => ({
        collision: c,
        rank: PARENT_SCOPE_RANK[String(c.id).split(":")[1]] || 0,
      }))
      .sort((a, b) => b.rank - a.rank);
    return [ranked[0].collision];
  }

  // Nothing actionable under the pointer (only excluded container items) → fall
  // back to the global nearest droppable.
  return closestCenter(args);
}
